import { makeObservable, observable, computed } from 'mobx';
import intersection from 'lodash.intersection';

import { fromPromise } from '../utils';
import { bugsnagClient } from '../bugsnag';
import config from '../config';

const enrichCosmosInfo = (info) => {
  if (!info || info.unreachable) {
    return info;
  }
  return {
    ...info,
    behindProcessing: (info.blockNumber - info.lastBlockProcessed) > 0
  };
};

const enrichEVMInfo = (info) => {
  if (!info || info.unreachable) {
    return info;
  }
  const enriched = { ...info };
  if (enriched.syncing) {
    enriched.highestKnownBlock = enriched.syncing.highestBlock;
  }
  enriched.behindProcessing = (enriched.blockNumber - enriched.lastBlockProcessed) > 0;
  enriched.behindBlocks = enriched.highestKnownBlock && (enriched.highestKnownBlock - enriched.lastBlockProcessed) > 0;
  enriched.behindBlockCount = enriched.highestKnownBlock && (enriched.highestKnownBlock - enriched.lastBlockProcessed) > 0
    ? enriched.highestKnownBlock - enriched.lastBlockProcessed
    : 0;
  return enriched;
};

const enrichNetworkInfo = (servicePath, rawInfo) => {
  const network = config.networks.find(n => n.servicePath === servicePath);
  if (network?.networkType === 'evm') {
    return enrichEVMInfo(rawInfo);
  }
  if (network?.networkType === 'cosmos') {
    return enrichCosmosInfo(rawInfo);
  }
  return rawInfo;
};

export default class Store {
  @observable authStatus = null;
  @observable user = null;
  @observable organization = null;
  @observable accessToken = null;
  @observable networkInfo = null;
  @observable info = null;
  @observable resellerId = null;
  @observable enabledNetworks = [];

  constructor (client) {
    makeObservable(this);
    this.client = client;
    this.authStatus = fromPromise(this.authenticate());

    this.client.service('verifications').on('removed', payload => {
      this.authenticate();
    });
  }

  @computed get isAuthenticated () {
    return this.user !== null && !this.user.disabledAt;
  }

  @computed get isAdmin () {
    return this.user && this.user.role && !!intersection(this.user.role.permissions, config.permissions.admin).length;
  }

  @computed get isReseller () {
    return this.user && this.user.role && !!intersection(this.user.role.permissions, config.permissions.reseller).length;
  }

  setOrganization (organizationId) {
    const { email, name, id, organizations } = this.user;
    this.organization = organizations.find(current => current.id === organizationId);

    if (!this.organization) return null;

    global.localStorage.setItem('last-organization', organizationId);

    bugsnagClient.user = { id, email, name, organizationId, organizationName: this.organization.name };

    if (process.browser && window.Beacon && config.helpscoutBeaconId) {
      if (this.isReseller) {
        // Remove the beacon for now so a reseller isn't creating themselves tickets
        window.Beacon('destroy');
      } else {
        const identifyProps = {
          name,
          email,
          company: this.organization.name,
          companyId: id,
          reseller: config.reseller
        };
        window.Beacon('identify', identifyProps);
      }
    }
    return this.organization;
  }

  async setUser (payload) {
    const userId = payload.user.id;
    const storedOrg = global.localStorage.getItem('last-organization');

    // TODO: Limiting this to 100 is eventually going to be an issue for Admins / Resellers so we should
    // re-evaluate how we allow people to switch organizatons, etc.
    this.user = {
      ...await this.client.service('users').get(userId),
      organizations: (await this.client.service('organizations').find({ query: { $limit: 100 } })).data
    };

    if (this.user.organizations.length) {
      if (this.user.organizationIds.includes(storedOrg)) {
        this.setOrganization(storedOrg);
      } else {
        this.setOrganization(this.user.organizations[0].id);
      }
    }

    /* istanbul ignore next */ // ignoring admin stuff
    if (this.isAdmin) {
      this.loadNetworkInfo();
      setInterval(this.loadNetworkInfo, config.blockchainPollingInterval);
    }

    return this.user;
  }

  async authenticate () {
    const payload = await this.client.reAuthenticate();
    this.accessToken = payload.accessToken;
    this.resellerId = payload.user.resellerId;
    return this.setUser(payload);
  }

  async login (credentials) {
    const payload = await this.client.authenticate({ ...credentials, strategy: 'local' });
    return this.setUser(payload);
  }

  async signup (data) {
    const payload = {
      reseller: config.reseller,
      ...data
    };
    await this.client.service('users').create(payload);
    const { email, password } = data;

    return await this.login({ email, password });
  }

  clearUser () {
    this.user = null;
    this.organization = null;
    this.accessToken = null;
    this.resellerId = null;
  }

  async logout () {
    await this.client.logout();
    this.clearUser();
  }

  async resetPassword (email) {
    await this.client.service('verification-request')
      .create({ email, type: 'reset-password' });
  }

  async setNewPassword (verificationId, password) {
    await this.client.service('verification-request')
      .patch(verificationId, { password, type: 'reset-password' });
  }

  /* istanbul ignore next */
  loadNetworkInfo = async () => {
    try {
      const {
        enabledNetworks,
        networkInfo = {},
        ...info
      } = await this.client.service('blockchain/info').find();

      this.enabledNetworks = enabledNetworks || [];
      this.info = info;
      this.networkInfo = Object.fromEntries(
        Object.entries(networkInfo).map(([servicePath, rawInfo]) => [
          servicePath,
          enrichNetworkInfo(servicePath, rawInfo)
        ])
      );
    } catch (e) {
      console.log(e.message);
    }
  };
}

export { enrichCosmosInfo, enrichEVMInfo, enrichNetworkInfo };
